import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { PlanCard } from "./PlanCard"
import { useState, useEffect } from "react"
import { detectAdBlocker } from "@/utils/adBlockDetector"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertTriangle, Sparkles } from "lucide-react"
import { useSession } from "@/hooks/use-session"
import { Button } from "@/components/ui/button"

interface PricingModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isTrialExpired?: boolean
  source?: string
  offer?: {
    discount: string
    expiresIn: string
    message: string
  } | null
}

export const PricingModal = ({ open, onOpenChange, isTrialExpired = false, source = 'direct', offer = null }: PricingModalProps) => {
  const { toast } = useToast()
  const { session } = useSession()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly')
  const [adBlockerDetected, setAdBlockerDetected] = useState(false)

  useEffect(() => {
    const checkAdBlocker = async () => {
      if (open) {
        const isBlocked = await detectAdBlocker()
        setAdBlockerDetected(isBlocked)
        console.log('[PricingModal] Ad blocker detection result:', isBlocked);
      }
    }
    checkAdBlocker()
  }, [open])

  const handleCheckout = async (priceId: string) => {
    try {
      if (!session?.access_token) {
        console.error('[PricingModal] No access token available');
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "Please sign in again to continue.",
        });
        return;
      }

      if (adBlockerDetected) {
        toast({
          variant: "destructive",
          title: "Ad Blocker Detected",
          description: "Please disable your ad blocker to proceed with the checkout.",
        })
        return
      }

      setIsLoading(true)
      console.log('[PricingModal] Starting checkout process...', {
        priceId,
        sessionId: session.access_token.slice(-6),
        userId: session.user.id
      });
      
      // Type-safe access to window property
      const referral = typeof window !== 'undefined' ? 
        (window as Window & { promotekit_referral?: string }).promotekit_referral : 
        undefined;
      console.log('[PricingModal] PromoteKit referral:', referral);

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          priceId,
          referral 
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })
      
      if (error) {
        console.error('[PricingModal] Supabase function error:', error);
        throw error;
      }
      
      if (data?.error) {
        console.error('[PricingModal] Error from checkout function:', data.error);
        throw new Error(data.error);
      }
      
      if (!data?.url) {
        console.error('[PricingModal] No checkout URL in response:', data);
        throw new Error('Failed to create checkout session');
      }
      
      console.log('[PricingModal] Redirecting to Stripe checkout:', data.url);
      window.location.href = data.url;
      
    } catch (error: unknown) {
      console.error('[PricingModal] Error creating checkout session:', error);
      
      let errorMessage = "Failed to start checkout process. Please try again.";
      
      // Type-safe error handling
      if (error instanceof Error) {
        if (error.message?.includes('Auth session')) {
          errorMessage = "Please sign in again to continue with checkout.";
        } else if (error.message?.includes('stripe')) {
          errorMessage = "There was an issue with the payment processor. Please try again.";
        }
      }
      
      // Check for status property on error object (API/response errors)
      if (typeof error === 'object' && error !== null && 'status' in error && error.status === 401) {
        errorMessage = "Your session has expired. Please sign in again.";
      }
      
      toast({
        variant: "destructive",
        title: "Checkout Error",
        description: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const plans = [
    {
      name: "Freemium",
      description: "Basic Trading Analytics",
      price: "Free",
      features: [
        "Limited AI Market Analysis (5 queries/day)",
        "Basic Technical Indicators",
        "Standard Market Calendar",
        "Core Trading Analytics"
      ],
      actionLabel: "Continue with Free",
      onAction: () => onOpenChange(false),
      disabled: false
    },
    {
      name: "Premium",
      description: "Make Smarter Trades with AI",
      price: selectedPlan === 'monthly' ? "$4.99" : "$49.99",
      originalPrice: selectedPlan === 'yearly' ? "$59.88" : undefined,
      period: selectedPlan === 'monthly' ? "/month" : "/year",
      popular: true,
      features: [
        "Unlimited AI Market Analysis",
        "Real-time Volatility Predictions",
        "Advanced Technical Indicators",
        "Premium Features",
        "Priority Support"
      ],
      benefits: [
        "Full Feature Access",
        "Cancel Anytime",
        "Priority Support"
      ],
      actionLabel: "Upgrade Now",
      onAction: () => handleCheckout(
        selectedPlan === 'monthly' 
          ? 'price_1RBCrDBtnCVyDPq8hHzBmzUv'  // TODO: Replace with new Monthly price ID from new Stripe account ($4.99)
          : 'price_1RBCsBBtnCVyDPq8mHb7jT1C'  // TODO: Replace with new Yearly price ID from new Stripe account ($49.99)
      ),
      isLoading: isLoading
    },
    {
      name: "Professional",
      description: "Advanced Trading Suite",
      price: "Coming Soon",
      originalPrice: undefined,
      period: "",
      popular: false,
      comingSoon: true,
      features: [
        "Everything in Premium",
        "Advanced AI Models & Signals",
        "Custom Alerts & Notifications",
        "API Access & Integrations",
        "Portfolio Management Tools",
        "Dedicated Account Manager"
      ],
      benefits: [
        "Enterprise Features",
        "API Access",
        "Dedicated Support"
      ],
      actionLabel: "Notify Me",
      onAction: () => {
        toast({
          title: "Thanks for your interest!",
          description: "We'll notify you when the Professional plan becomes available.",
        });
      },
      disabled: true,
      isLoading: false
    }
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full h-full max-w-none max-h-none p-0 border-0 bg-transparent overflow-hidden">
        {/* Full-screen modal with background treatment matching Auth/Onboarding */}
        <div className="relative flex w-full h-[100dvh] items-center justify-center overflow-hidden px-4 sm:px-6 bg-gradient-to-br from-gray-900 via-blue-900/20 to-gray-900">
          {/* Background decorations */}
          <div 
            className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-10 pointer-events-none" 
            aria-hidden="true"
          />
          <div 
            className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-blue-500/10 blur-3xl pointer-events-none motion-reduce:opacity-0" 
            aria-hidden="true"
          />
          <div 
            className="absolute top-0 left-0 h-[28rem] w-[28rem] rounded-full bg-purple-500/10 blur-3xl pointer-events-none motion-reduce:opacity-0" 
            aria-hidden="true"
          />

          {/* Close button */}
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-gray-800/50 hover:bg-gray-700/50 border border-gray-600/50 backdrop-blur-sm transition-colors"
            aria-label="Close pricing modal"
          >
            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Main content container */}
          <div className="relative z-10 w-full max-w-sm sm:max-w-md lg:max-w-4xl xl:max-w-5xl">
            <div className="bg-black/10 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-xl lg:shadow-2xl border border-gray-800/40 overflow-hidden">
              <div className="p-6 sm:p-8 lg:p-10">
                {/* Header Section */}
                <div className="text-center space-y-3 sm:space-y-5 mb-6 sm:mb-8">
                  <div className="flex items-center justify-center gap-3 mb-3 sm:mb-5">
                    <div className="flex h-4 w-4 sm:h-5 sm:w-5 items-center justify-center rounded-full bg-blue-600/20 backdrop-blur-sm border border-blue-500/30">
                      <img src="/strykr-logo.jpeg" alt="Strykr.ai Logo" className="h-4 w-4 sm:h-5 sm:w-5 rounded-full" />
                    </div>
                    <h1 className="text-lg sm:text-xl font-bold text-white">
                      Strykr Ai
                    </h1>
                  </div>
                  
                  <div className="space-y-2 sm:space-y-3">
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight">
                      {isTrialExpired ? "Trial Expired - Choose Your Plan" : "Choose Your Plan"}
                    </h2>
                    
                    {isTrialExpired && (
                      <p className="text-blue-200/80 text-xs sm:text-sm max-w-2xl mx-auto">
                        Your 7-day trial has ended. You can continue with our free plan or upgrade to Premium for unlimited access.
                      </p>
                    )}
                    
                    {offer && (
                      <div className="inline-block bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-400/30 rounded-lg px-3 py-2 backdrop-blur-sm">
                        <p className="text-xs sm:text-sm font-medium text-yellow-300">{offer.message}</p>
                        <p className="text-xs text-yellow-200/80">
                          {offer.discount} off - expires in {offer.expiresIn}
                        </p>
                      </div>
                    )}
                    
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/20 to-blue-500/20 border border-emerald-400/30 px-3 py-1.5 rounded-full backdrop-blur-sm">
                      <Sparkles className="h-3 w-3 text-emerald-400" />
                      <span className="text-xs text-emerald-300 font-medium">Trusted by 1,000+ traders - 50% off</span>
                    </div>
                  </div>
                </div>

                {adBlockerDetected && (
                  <Alert variant="destructive" className="mb-6 bg-red-900/20 border-red-500/30 text-red-200">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Please disable your ad blocker to proceed with the checkout. Some browser extensions might block Stripe's payment system.
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Billing Toggle */}
                <div className="flex justify-center mb-6 sm:mb-8">
                  <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-1 backdrop-blur-sm">
                    <button
                      onClick={() => setSelectedPlan('monthly')}
                      className={`px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                        selectedPlan === 'monthly'
                          ? 'bg-gradient-to-r from-emerald-600 to-blue-600 text-white shadow-lg'
                          : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                      }`}
                    >
                      Monthly Billing
                    </button>
                    <button
                      onClick={() => setSelectedPlan('yearly')}
                      className={`px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all relative ${
                        selectedPlan === 'yearly'
                          ? 'bg-gradient-to-r from-emerald-600 to-blue-600 text-white shadow-lg'
                          : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                      }`}
                    >
                      Yearly Billing
                      <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                        Save 17%
                      </span>
                    </button>
                  </div>
                </div>
                
                {/* Plans Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-5 max-w-6xl mx-auto mb-6">
                  {plans.map((plan, index) => (
                    <div 
                      key={index}
                      className={`relative bg-gray-900/60 border rounded-xl p-5 sm:p-6 backdrop-blur-sm transition-all hover:scale-105 ${
                        plan.popular 
                          ? 'border-emerald-500/50 shadow-lg shadow-emerald-500/20' 
                          : 'border-gray-700/50 hover:border-gray-600/50'
                      }`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                          <div className="bg-gradient-to-r from-emerald-500 to-blue-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                            Most Popular
                          </div>
                        </div>
                      )}
                      
                      <div className="text-center space-y-3 sm:space-y-5">
                        <div>
                          <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5">{plan.name}</h3>
                          <p className="text-gray-400 text-xs sm:text-sm">{plan.description}</p>
                        </div>
                        
                        <div className="space-y-1.5">
                          <div className="text-2xl sm:text-3xl font-bold text-white">
                            <span className={plan.comingSoon ? "line-through text-gray-500" : ""}>
                              {plan.price}
                            </span>
                            {plan.period && <span className="text-base text-gray-400">{plan.period}</span>}
                          </div>
                          {plan.originalPrice && (
                            <div className="text-sm text-gray-400 line-through">{plan.originalPrice}</div>
                          )}
                        </div>
                        
                        <ul className="space-y-2.5 text-left">
                          {plan.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-center gap-2.5 text-gray-300">
                              <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span className="text-xs sm:text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <Button 
                          onClick={plan.onAction}
                          disabled={plan.disabled || plan.isLoading}
                          className={`w-full py-2.5 sm:py-3 text-sm font-medium rounded-lg transition-all ${
                            plan.popular
                              ? 'bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 shadow-lg shadow-emerald-500/20 text-white border border-emerald-400/30'
                              : 'bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-100'
                          }`}
                          style={plan.popular ? { 
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)' 
                          } : {}}
                        >
                          {plan.isLoading ? "Processing..." : plan.actionLabel}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Footer */}
                <div className="text-center">
                  <Button 
                    variant="link" 
                    className="text-blue-400 hover:text-blue-300 font-normal" 
                    onClick={() => {
                      onOpenChange(false);
                      window.location.href = '/pricing';
                    }}
                  >
                    See full pricing details and feature comparison →
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}